import React, { useEffect, useState, useCallback } from "react";
import { resolveImage } from "../lib/api";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, openIndex, onClose }) {
  const [index, setIndex] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null && openIndex !== undefined) setIndex(openIndex);
  }, [openIndex]);

  const close = useCallback(() => onClose && onClose(), [onClose]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (openIndex === null || openIndex === undefined) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openIndex, close, prev, next]);

  if (openIndex === null || openIndex === undefined) return null;
  if (!images || images.length === 0) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={close}
    >
      <button
        type="button"
        onClick={close}
        data-testid="lightbox-close"
        className="absolute top-6 right-6 z-10 text-[#F9F6F0] hover:text-[#C5A880] p-2"
        aria-label="Close"
      >
        <X className="w-7 h-7" strokeWidth={1.25} />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            data-testid="lightbox-prev"
            className="absolute left-6 z-10 text-[#F9F6F0] hover:text-[#C5A880] p-3"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8" strokeWidth={1.25} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            data-testid="lightbox-next"
            className="absolute right-6 z-10 text-[#F9F6F0] hover:text-[#C5A880] p-3"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8" strokeWidth={1.25} />
          </button>
        </>
      )}

      <div
        className="relative max-w-[92vw] max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={resolveImage(images[index])}
          alt={`Gallery ${index + 1}`}
          className="max-w-full max-h-[88vh] object-contain"
        />
        {images.length > 1 && (
          <div className="absolute bottom-[-44px] left-1/2 -translate-x-1/2 text-[#F9F6F0]/80 text-[10px] tracking-[0.3em] uppercase">
            {index + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}
